import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, NgModule } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { ClassroomFormWrapperModule } from '@app/classrooms/_components/classroom-form-wrapper/classroom-form-wrapper.module';
import { InviteToClassroomModule } from '@app/classrooms/_components/invite-to-classroom/invite-to-classroom.module';
import { loadClassroomParticipants } from '@app/classrooms/_store/participants';
import { ModalModule } from '@app/core/components/modal/modal.module';
import {
  ClassroomsService,
  FeaturesService,
  FilesService,
  PlatformService,
  UserServiceContractsService,
} from '@app/core/services';
import { BreakContractReasonData, classBreakReasonsData } from '@app/library/panes/my-classes-classrooms/constants';
import { AlertModule } from '@core/components/alert/alert.module';
import { MobileModalModule } from '@core/components/mobile-modal/mobile-modal.module';
import { PinService } from '@core/services/lingo2-content/pin.service';
import { ChangableComponent } from '@models/changable.component';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { ErrorNotificationService } from '@shared/error-notification/error-notification.service';
import { IconComponent } from '@shared/icon/icon.component';
import { createPin, removePin } from '@store/actions/profile.actions';
import {
  AccessStatusEnum,
  AnyType,
  BreakUserServiceContractRequest,
  Classroom,
  EContentPanelRoute,
  EntityTypeEnum,
  FeatureEnum,
  ImageSizeEnum,
  PinnedEntity,
  User,
  UserService,
  UserServiceContract,
} from 'lingo2-models';
import { EUserServiceType } from 'lingo2-models/dist/content/user-service/user-service-type-enum';
import { isBoolean } from 'lodash';
import { DeviceDetectorService } from 'ngx-device-detector';
import { OnUiButtonModule, OnUiCardMenuModule, OnUiCover, OnUiCoverModule, OnUiPopoverModule } from 'onclass-ui';
import { takeUntil, tap } from 'rxjs';
import { classroomDetails, menuOptions, VISIBLE_AVATARS_COUNT } from '../../constants';
import { ClassroomCardActionsEnum, IClassroomCardOption } from '../../models';

@Component({
  selector: 'app-classroom-card',
  templateUrl: './classroom-card.component.html',
  styleUrls: ['./classroom-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClassroomCardComponent extends ChangableComponent {
  @Input()
  public classroom: Classroom;

  @Input()
  protected pins: PinnedEntity[];

  @Input()
  protected me: User;

  public options = menuOptions;
  public menuPopoverOpened = false;
  public inviteModal = false;
  public classroomModalOpened = false;
  public classroomRoute = ClassroomsService.classroomRoute;
  public EUserServiceType = EUserServiceType;

  public classBreakAlertShow = false;
  public classBreakReasons: 'no_classes' | 'left_classes' | 'less_24_hours' = 'no_classes';
  public alertData: BreakContractReasonData = null;

  public constructor(
    public errorNotificationService: ErrorNotificationService,
    public deviceService: DeviceDetectorService,
    private router: Router,
    private classroomsService: ClassroomsService,
    private store: Store,
    private pinService: PinService,
    private features: FeaturesService,
    private contractsService: UserServiceContractsService,
    protected readonly cdr: ChangeDetectorRef,
    protected readonly platform: PlatformService,
  ) {
    super(cdr, platform);
  }

  public get isPined(): boolean {
    const pinnedEntityIds = (this.pins || []).map((x) => x.entity_id);
    return !!this.classroom?.id && pinnedEntityIds.includes(this.classroom?.id);
  }

  public get userService(): UserService {
    return this.classroom?.user_service;
  }

  public get contract(): UserServiceContract {
    return this.classroom.user_service_contract;
  }

  public get participants() {
    if (!this.classroom || !this.classroom.participants) {
      return [];
    }
    return [...this.classroom.participants].splice(0, VISIBLE_AVATARS_COUNT);
  }

  public get otherParticipantsCount(): number {
    const count = this.classroom.participants.length - VISIBLE_AVATARS_COUNT;
    return count > 0 ? count : null;
  }

  public get title(): string {
    const isNoTitle = !this.classroom.title || this.classroom.title === '---';
    return isNoTitle ? 'classrooms.classroom.title' : this.classroom.title;
  }

  public coverImg(): OnUiCover {
    return {
      type: 'cover',
      img:
        FilesService.getFileBySize(this.classroom.cover, ImageSizeEnum.wide)?.url ||
        FilesService.getFileUrlBySize(this.classroom.cover_id, ImageSizeEnum.wide),
      title: this.classroom?.title,
      link: this.classroomRoute(this.classroom).join('/'),
      aspect: '16/9',
    };
  }

  public trackByFn(user: User): string {
    return user.id;
  }

  public isEnabledMenuOption(option: IClassroomCardOption): boolean {
    if (!('enabled' in option)) {
      return true;
    }
    if (isBoolean(option.enabled)) {
      return Boolean(option.enabled);
    }

    if (option.id === ClassroomCardActionsEnum.pin || option.id === ClassroomCardActionsEnum.unpin) {
      return false;
    }

    return (option.enabled as AnyType)(this.classroom, { is_pined: this.isPined });
  }

  public isPinAvailable() {
    return this.features.isAvailable(FeatureEnum.pins);
  }

  public get isMe(): boolean {
    return this.me?.id === this.classroom?.author_id;
  }

  public get isPrivate(): boolean {
    return this.classroom?.access === AccessStatusEnum.private;
  }

  public onAction(option: IClassroomCardOption): void {
    if (!this.isEnabledMenuOption(option)) {
      return;
    }
    this.closeMenuPopover();

    switch (option.id) {
      case ClassroomCardActionsEnum.invite:
        return this.openInviteModal();

      case ClassroomCardActionsEnum.pin:
        return this.pinClassroom();

      case ClassroomCardActionsEnum.unpin:
        return this.unpinClassroom();

      case ClassroomCardActionsEnum.archive:
        return this.archiveClassroom();

      case ClassroomCardActionsEnum.restore:
        return this.restoreClassroom();

      case ClassroomCardActionsEnum.leave:
        return this.leaveClassroom();

      case ClassroomCardActionsEnum.break_contract:
        return this.breakUserServiceContract();

      case ClassroomCardActionsEnum.edit:
        return this.onToggleEditForm();

      default:
        const _id = ClassroomCardActionsEnum[option.id] || '?';
        alert(`Action ${_id} not supported`);
        break;
    }
  }

  public openMenuPopover(isOpened: boolean) {
    this.menuPopoverOpened = isOpened;
    this.detectChanges();
  }

  public closeMenuPopover() {
    this.menuPopoverOpened = false;
    this.detectChanges();
  }

  public openInviteModal() {
    this.inviteModal = true;
    this.detectChanges();
  }

  public closeInviteModal() {
    this.inviteModal = false;
    this.detectChanges();
  }

  public onParticipantsInvited() {
    this.store.dispatch(loadClassroomParticipants());
  }

  public onToggleEditForm() {
    this.classroomModalOpened = !this.classroomModalOpened;
    this.detectChanges();
  }

  public onSavedClassroom() {
    this.classroomsService
      .getClassroomById(this.classroom.id, classroomDetails)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (classroom) => {
          this.classroom = classroom;
          this.onToggleEditForm();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  private leaveClassroom(): void {
    this.unpinClassroom();

    this.classroomsService
      .leaveClassroom(this.classroom.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => this.router.navigate([EContentPanelRoute['my-classes/classrooms']]).then(() => true),
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  private breakUserServiceContract() {
    if (!this.classroom.user_service_contract_id) {
      return;
    }

    this.unpinClassroom();

    const request = new BreakUserServiceContractRequest({
      user_service_contract_id: this.classroom.user_service_contract_id,
      reason: null, // TODO
      comment: null, // TODO
    });
    this.contractsService
      .breakContract(request)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.classBreakAlertShow = false;
          this.detectChanges();
          this.router.navigate([EContentPanelRoute['my-classes/classrooms']]).then(() => true);
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'OTHER-PROBLEM');
        },
      });
  }

  private pinClassroom(): void {
    const pinedEntity = (this.pins || []).find((p) => p.entity_id === this.classroom.id);
    if (!pinedEntity) {
      const pin: Partial<PinnedEntity> = {
        type: EntityTypeEnum.classroom,
        entity_id: this.classroom.id,
        title: this.classroom.title,
        image_id: this.classroom.cover_id,
      };
      this.store.dispatch(createPin({ pin }));
    }
  }

  private unpinClassroom() {
    const pinedEntity = (this.pins || []).find((p) => p.entity_id === this.classroom.id);
    if (pinedEntity) {
      this.store.dispatch(removePin({ pin_id: pinedEntity.id }));
    }
  }

  private archiveClassroom(): void {
    this.classroomsService
      .deleteClassroom(this.classroom.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'SAVE-PROBLEM');
        },
      });
  }

  private restoreClassroom(): void {
    this.classroomsService
      .restoreClassroom(this.classroom.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'SAVE-PROBLEM');
        },
      });
  }

  public onContractBreak() {
    // @TODO: Сделать вычисление по какой причине показывается алерт
    this.classBreakReasons = 'no_classes';

    if (this.classroom.user_service) {
      if (this.classroom.user_service.type === EUserServiceType.regular) {
        this.alertData = classBreakReasonsData['regular'][this.classBreakReasons];
      } else if (
        this.classroom.user_service.type === EUserServiceType.course ||
        this.classroom.user_service.type === EUserServiceType.mini
      ) {
        this.alertData = classBreakReasonsData['course'][this.classBreakReasons];
      } else {
        this.alertData = null;
      }
    } else {
      this.alertData = null;
    }

    this.menuPopoverOpened = false;
    this.classBreakAlertShow = true;
    this.detectChanges();
  }

  public breakContract() {
    if (!this.classroom.user_service_contract_id) {
      return;
    }

    this.unpinClassroom();

    const request = new BreakUserServiceContractRequest({
      user_service_contract_id: this.classroom.user_service_contract_id,
      reason: null, // TODO
      comment: null, // TODO
    });
    this.contractsService
      .breakContract(request)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.classBreakAlertShow = false;
          this.detectChanges();
          this.router.navigate([EContentPanelRoute['my-classes/classrooms']]).then(() => true);
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'OTHER-PROBLEM');
          this.classBreakAlertShow = false;
        },
      });
  }
}

@NgModule({
  declarations: [ClassroomCardComponent],
  imports: [
    CommonModule,
    ModalModule,
    TranslateModule,
    RouterModule,
    IconComponent,
    OnUiPopoverModule,
    OnUiCoverModule,
    InviteToClassroomModule,
    MobileModalModule,
    OnUiButtonModule,
    AlertModule,
    ClassroomFormWrapperModule,
    OnUiCardMenuModule,
    IconComponent,
  ],
  exports: [ClassroomCardComponent],
})
export class ClassroomCardModule {}
